import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type BulkErrors,
  type BulkFields,
  validateBulkFields,
} from "../../../utils/edit-workout";
import SvgIcon from "../../SvgIcon";

type Section = { id: string; name: string };

type Props = {
  sections: Section[];
  loadUnit: string;
  onApply: (fields: BulkFields, scope: string) => Promise<number | null>;
};

const FIELDS: Array<keyof BulkFields> = [
  "rpe",
  "rest",
  "tempo",
  "load",
  "sets",
  "reps",
];

const PLACEHOLDERS: Record<keyof BulkFields, string> = {
  rpe: "8.5",
  rest: "1:30",
  tempo: "3010",
  load: "0",
  sets: "3",
  reps: "10",
};

/**
 * Edit Workout's bulk editor: writes the filled fields into every set row of
 * every exercise in scope. Nothing is saved until Save workout.
 */
const ChangeManyAtOnce = ({ sections, loadUnit, onApply }: Props) => {
  const { t } = useTranslation("coaching-portal");
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState<BulkFields>({});
  const [scope, setScope] = useState("all");
  const [errors, setErrors] = useState<BulkErrors>({});

  const anyFilled = FIELDS.some((key) => fields[key]?.trim());

  const apply = async () => {
    const found = validateBulkFields(fields);
    setErrors(found);
    if (Object.keys(found).length) return;
    const affected = await onApply(fields, scope);
    if (affected !== null) setFields({});
  };

  return (
    <div className="wq-bulk">
      <button
        type="button"
        className="wq-bulk-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {/* currentColor icons: an <img> would draw them black in dark theme. */}
        <SvgIcon
          src="/img/new-icons/magic-wand.svg"
          useCurrentColor
          classNames="size-4"
        />
        {t("editWorkout.bulk.title")}
        <SvgIcon
          src="/img/new-icons/chevron-down.svg"
          useCurrentColor
          classNames={`size-4${open ? " rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="wq-bulk-body">
          <div className="wq-bulk-grid">
            {FIELDS.map((key) => (
              <label key={key} className="wq-bulk-field">
                <span>{t(`editWorkout.bulk.${key}`, { unit: loadUnit })}</span>
                <input
                  className="text-input"
                  value={fields[key] ?? ""}
                  placeholder={PLACEHOLDERS[key]}
                  inputMode={
                    ["tempo", "rest"].includes(key) ? "text" : "decimal"
                  }
                  maxLength={key === "tempo" ? 4 : 6}
                  // `false` would draw semantic.css's valid checkmark on
                  // empty, optional fields.
                  aria-invalid={errors[key] ? true : undefined}
                  onChange={(e) =>
                    setFields({ ...fields, [key]: e.target.value })
                  }
                />
                {errors[key] && (
                  <span className="text-(--danger) text-xs">
                    {t(`editWorkout.bulkErrors.${errors[key]}`)}
                  </span>
                )}
              </label>
            ))}
          </div>
          <label className="wq-bulk-field">
            <span>{t("editWorkout.bulk.applyTo")}</span>
            <select
              className="text-input"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <option value="all">{t("editWorkout.bulk.wholeWorkout")}</option>
              {sections.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-button-type="secondary"
            disabled={!anyFilled}
            onClick={apply}
          >
            {t("editWorkout.bulk.apply")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ChangeManyAtOnce;
